import React from 'react'
import { MapPinIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { normalizeSocialLinks } from '../../../utils/social'
import { buildCloudinaryUrl, buildCloudinarySrcSet } from '../../../utils/cloudinaryImage'
import useCardPWA from '../../../pwa/useCardPWA'
import PWAInstallButton from '../../../components/pwa/PWAInstallButton'

function LandingTemplate({ vCard, template }) {
  const theme = vCard.theme || {
    primaryColor: '#f59e0b',
    accentColor: '#6b7280',
    font: 'Inter'
  }

  const socialLinks = normalizeSocialLinks(vCard.social || {})
  useCardPWA(vCard)

  return (
    <div 
      className="landing-template min-h-screen bg-gray-50"
      style={{ fontFamily: theme.font }}
    >
      {/* Hero Section */}
      <div className="relative h-screen flex items-center justify-center" style={{ backgroundImage: 'linear-gradient(to bottom right, #C14E8E, #05C0FB)' }}>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative z-10 text-center text-white px-6">
          <img
            src={buildCloudinaryUrl(vCard.photoUrl || 'https://via.placeholder.com/150x150/e2e8f0/64748b?text=Foto', { w: 160, h: 160, crop: 'fill' })}
            srcSet={buildCloudinarySrcSet(vCard.photoUrl || 'https://via.placeholder.com/150x150/e2e8f0/64748b?text=Foto', [80, 120, 160])}
            sizes="(max-width: 768px) 120px, 160px"
            alt={vCard.name}
            className="w-32 h-32 rounded-full border-4 border-white shadow-2xl mx-auto mb-6 object-cover"
            loading="lazy"
          />
          <h1 className="text-4xl md:text-6xl font-bold mb-2">{vCard.name}</h1>
          <p className="text-xl md:text-2xl text-amber-100 mb-8">{vCard.position}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="px-8 py-4 bg-white text-amber-600 rounded-full font-semibold hover:scale-105 transition-transform shadow-lg"
              onClick={() => window.open(`tel:${vCard.phone}`, '_self')}
            >
              📞 Llamar Ahora
            </button>
            <button
              className="px-8 py-4 bg-amber-600 text-white rounded-full font-semibold hover:scale-105 transition-transform shadow-lg"
              onClick={() => window.open(socialLinks.whatsapp, '_blank')}
            >
              💬 WhatsApp
            </button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* About Section */}
      {vCard.bio && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Sobre Mí</h2>
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              {vCard.bio}
            </p>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Servicios</h2>
          {Array.isArray(vCard.services) && vCard.services.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {vCard.services.map((svc) => (
                <div key={svc.id} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: theme.primaryColor + '20' }}>
                    <span className="text-2xl" style={{ color: theme.primaryColor }}>{svc.icon || '💼'}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{svc.title}</h3>
                  {svc.desc && <p className="text-gray-600 text-center">{svc.desc}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: theme.primaryColor + '20' }}>
                  <PhoneIcon className="w-8 h-8" style={{ color: theme.primaryColor }} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Consultoría</h3>
                <p className="text-gray-600 text-center">Asesoramiento personalizado para tu negocio o proyecto.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: theme.primaryColor + '20' }}>
                  <EnvelopeIcon className="w-8 h-8" style={{ color: theme.primaryColor }} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Formación</h3>
                <p className="text-gray-600 text-center">Cursos y talleres especializados para tu equipo.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: theme.primaryColor + '20' }}>
                  <MapPinIcon className="w-8 h-8" style={{ color: theme.primaryColor }} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Implementación</h3>
                <p className="text-gray-600 text-center">Puesta en marcha de soluciones a medida.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Section */}
      {Array.isArray(vCard.gallery) && vCard.gallery.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Galería</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
              {vCard.gallery.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <img
                    src={buildCloudinaryUrl(item.imageUrl || 'https://via.placeholder.com/400x300/e2e8f0/64748b?text=Imagen', { w: 600, h: 400, crop: 'fill' })}
                    srcSet={buildCloudinarySrcSet(item.imageUrl || 'https://via.placeholder.com/400x300/e2e8f0/64748b?text=Imagen', [300, 450, 600])}
                    sizes="(max-width: 768px) 50vw, 33vw"
                    alt={item.title || 'Imagen'}
                    className="w-full h-40 md:h-48 object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/400x300/e2e8f0/64748b?text=Imagen' }}
                  />
                  {item.title && <div className="p-4"><p className="text-sm text-gray-700 truncate">{item.title}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Contacto</h2>
          <p className="text-lg text-gray-700 mb-12">¿Listo para comenzar? ¡Hablemos!</p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {vCard.phone && (
              <a
                href={`tel:${vCard.phone}`}
                className="flex items-center justify-center p-6 border-2 border-gray-200 rounded-2xl hover:border-amber-400 transition-colors group"
              >
                <PhoneIcon className="w-8 h-8 text-gray-600 group-hover:text-amber-500 mr-4" />
                <div>
                  <p className="font-semibold text-gray-900">Teléfono</p>
                  <p className="text-gray-600">{vCard.phone}</p>
                </div>
              </a>
            )}
            
            {vCard.email && (
              <a
                href={`mailto:${vCard.email}`}
                className="flex items-center justify-center p-6 border-2 border-gray-200 rounded-2xl hover:border-amber-400 transition-colors group"
              >
                <EnvelopeIcon className="w-8 h-8 text-gray-600 group-hover:text-amber-500 mr-4" />
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <p className="text-gray-600">{vCard.email}</p>
                </div>
              </a>
            )}
          </div>
        </div>
      </section>

  <PWAInstallButton vCard={vCard} />

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center space-x-6 mb-8">
            {socialLinks.whatsapp && (
              <a
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center hover:scale-110 transition-transform"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.645 3.488"/>
                </svg>
              </a>
            )}
            
            {socialLinks.linkedin && (
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center hover:scale-110 transition-transform"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            )}
            
            {socialLinks.instagram && (
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center hover:scale-110 transition-transform"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                </svg>
              </a>
            )}
          </div>
          
          <p className="text-gray-400">
            © 2024 {vCard.name}. Todos los derechos reservados.
          </p>
        </div>
      </footer>
  </div>
  )
}

LandingTemplate.propTypes = {
  vCard: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
}

export default LandingTemplate
